import { RouteProp, useRoute } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { Provider, useAtomValue, useSetAtom } from "jotai";
import { Suspense, useCallback, useEffect } from "react";
import { StatusBar } from "react-native";
import { useSharedValue } from "react-native-reanimated";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { useGetClubStartOfWeekNumberPatron } from "@/hooks/useGetClubStartOfWeekNumberPatron";
import { AgendaLayout } from "@whoosh/design-system/src/apps/PatronApp/layout/AgendaLayout";
import { Loader } from "@whoosh/design-system/src/components/display";
import { Box } from "@whoosh/design-system/src/components/primitive";

import { VerifiedAuthenticationModalAtomHydrator } from "../../../../features/Authentication/components/AuthenticationModal/VerifiedAuthenticationModal/components/VerifiedAuthenticationModalAtomHydrator";
import {
  agendaQueryInputs_courseBucketIds,
  agendaQueryInputs_duration,
  agendaQueryInputs_groupSize,
  agendaQueryInputs_times,
} from "../../../../features/DurationBasedAgendas/atoms/agendaQueryInputs";
import { DurationBasedAgenda } from "../../../../features/DurationBasedAgendas/components/DurationBasedAgenda";
import { DurationBasedAgendaHeader } from "../../../../features/DurationBasedAgendas/components/DurationBasedAgendaHeader";
import { DurationBasedAgendaSidebar } from "../../../../features/DurationBasedAgendas/components/DurationBasedAgendaSidebar";
import { useInitializeStoredCourseBucketFilter } from "../../../../features/DurationBasedAgendas/hooks/useInitializeStoredCourseBucketFilter";
import { useInitializeStoredDurationFilter } from "../../../../features/DurationBasedAgendas/hooks/useInitializeStoredDurationFilter";
import { useInitializeStoredGroupSizeFilter } from "../../../../features/DurationBasedAgendas/hooks/useInitializeStoredGroupSizeFilter";
import { useInitializeStoredTimeOfDayFilters } from "../../../../features/DurationBasedAgendas/hooks/useInitializeStoredTimeOfDayFilters";
import { sessionCurrentClubAtom } from "../../../../features/Session";
import { ClubStackNavigatorParamList } from "../../../../navigators/ClubNavigator";
import { DurationBasedAgendaScreenQuery } from "./__generated__/DurationBasedAgendaScreenQuery.graphql";

export const DurationBasedAgendaScreen = () => {
  const route = useRoute<RouteProp<ClubStackNavigatorParamList, "Agenda">>();

  const facilitySlug = route?.params?.facilitySlug;

  const [queryRef, loadQuery] = useQueryLoader<DurationBasedAgendaScreenQuery>(
    DurationBasedAgendaScreenQueryString
  );

  const fetchAgendaQuery = useCallback(() => {
    if (loadQuery && facilitySlug) {
      loadQuery({ facilitySlug }, { fetchPolicy: "store-and-network" });
    }
  }, [facilitySlug, loadQuery]);

  useEffect(() => {
    fetchAgendaQuery();
  }, [facilitySlug, fetchAgendaQuery, loadQuery]);

  const sessionClub = useAtomValue(sessionCurrentClubAtom);

  return (
    <Provider>
      {sessionClub ? (
        <VerifiedAuthenticationModalAtomHydrator sessionClub={sessionClub} />
      ) : null}
      <Box flex={1} safeAreaTop backgroundColor="surface-primary">
        <WhiteBoxToCoverHeaderShadow />
        <StatusBar
          animated={true}
          barStyle="dark-content"
          backgroundColor="white"
        />
        {queryRef ? (
          <Suspense fallback={<Loader />}>
            <DurationBasedAgendaScreenInner
              queryRef={queryRef}
              fetchAgendaQuery={fetchAgendaQuery}
            />
          </Suspense>
        ) : null}
      </Box>
    </Provider>
  );
};

type Props = {
  queryRef: PreloadedQuery<DurationBasedAgendaScreenQuery>;
  fetchAgendaQuery: () => void;
};

const DurationBasedAgendaScreenInner = ({
  queryRef,
  fetchAgendaQuery,
}: Props) => {
  const data = usePreloadedQuery(
    DurationBasedAgendaScreenQueryString,
    queryRef
  );
  const clubWeekStartsOn = useGetClubStartOfWeekNumberPatron(
    data.facility?.club ?? null
  );

  const scrollY = useSharedValue(0);
  const storedFiltersFacilityId = data.facility?.id ?? null;

  const setDuration = useSetAtom(agendaQueryInputs_duration);
  const setGroupSize = useSetAtom(agendaQueryInputs_groupSize);
  const setTimes = useSetAtom(agendaQueryInputs_times);
  const courseBucketIds = useAtomValue(agendaQueryInputs_courseBucketIds);
  const setCourseBucketIds = useSetAtom(agendaQueryInputs_courseBucketIds);

  useInitializeStoredDurationFilter(storedFiltersFacilityId, (value?: number) =>
    setDuration(value || 1)
  );
  useInitializeStoredGroupSizeFilter(
    storedFiltersFacilityId,
    (value?: number) => setGroupSize(value || 1)
  );
  useInitializeStoredTimeOfDayFilters(
    data.facility?.startEndTime?.startTime ?? null,
    data.facility?.startEndTime?.endTime ?? null,
    storedFiltersFacilityId,
    setTimes
  );
  useInitializeStoredCourseBucketFilter(
    storedFiltersFacilityId,
    courseBucketIds || [],
    (value?: string[]) => {
      setCourseBucketIds(value || []);
    }
  );

  return (
    <AgendaLayout
      shouldCenter
      header={
        <DurationBasedAgendaHeader
          facilityRef={data.facility ?? null}
          sessionRef={data.session ?? null}
          scrollY={scrollY}
          clubWeekStartsOn={clubWeekStartsOn}
        />
      }
      sidebarContent={
        <DurationBasedAgendaSidebar
          facilityRef={data.facility ?? null}
          sessionRef={data.session ?? null}
        />
      }
      agenda={
        <DurationBasedAgenda
          facilityRef={data.facility ?? null}
          sessionRef={data.session ?? null}
          refetchCallback={fetchAgendaQuery}
          scrollY={scrollY}
        />
      }
    />
  );
};

const DurationBasedAgendaScreenQueryString = graphql`
  query DurationBasedAgendaScreenQuery($facilitySlug: String!) {
    facility(slug: $facilitySlug) {
      id
      startEndTime {
        startTime
        endTime
      }
      ...DurationBasedAgendaHeaderFragment_facility
      ...DurationBasedAgendaFragment_facility
      ...DurationBasedAgendaSidebarFragment_facility
      club {
        ...useGetClubStartOfWeekNumberPatron_club
      }
    }
    session {
      ...DurationBasedAgendaHeaderFragment_session
      ...DurationBasedAgendaFragment_session
      ...DurationBasedAgendaSidebar_session
    }
  }
`;

// This is used to cover up the shadow of the header that appears in the status bar.
const WhiteBoxToCoverHeaderShadow = () => {
  return (
    <Box
      height={60}
      style={{ marginTop: -60, zIndex: 1 }}
      bg="surface-primary"
    ></Box>
  );
};
